import {
  ANNOUNCEMENT_MAX_LENGTH,
  ANNOUNCEMENTS_PER_ROOM,
  type LiveRoomAnnouncement,
  type LiveRoomAnnouncementResponse,
} from '@pulsify/shared/dto'
import { Megaphone } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { Button } from '~/shared/ui/button'
import { Label } from '~/shared/ui/label'
import { Textarea } from '~/shared/ui/textarea'
import { announcementErrorMessage } from '../lib/announcement-error'

export function AnnouncementBox({
  here,
  announcements,
  isSending,
  onSend,
}: {
  here: number
  announcements?: LiveRoomAnnouncement[]
  isSending: boolean
  onSend: (text: string) => Promise<LiveRoomAnnouncementResponse>
}) {
  const fieldId = useId()
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const limitReached = (announcements?.length ?? 0) >= ANNOUNCEMENTS_PER_ROOM
  const empty = text.trim().length === 0

  const handleSend = async () => {
    setError(null)
    try {
      const { recipientCount } = await onSend(text.trim())
      setText('')
      toast.success(m.roomAnnouncementSent(), {
        description: m.roomAnnouncementReached({ count: recipientCount }),
      })
    } catch (sendError) {
      setError(announcementErrorMessage(sendError))
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={fieldId}>{m.roomAnnouncementLabel()}</Label>
      <Textarea
        id={fieldId}
        value={text}
        maxLength={ANNOUNCEMENT_MAX_LENGTH}
        rows={3}
        placeholder={m.roomAnnouncementPlaceholder()}
        aria-invalid={error ? true : undefined}
        onChange={(event) => {
          setText(event.target.value)
          setError(null)
        }}
        disabled={limitReached}
      />
      <div className="flex items-start justify-between gap-2 text-xs">
        <p className={error ? 'text-destructive' : 'text-muted-foreground'}>
          {error ??
            (limitReached
              ? m.roomAnnouncementLimit({ count: ANNOUNCEMENTS_PER_ROOM })
              : here === 0
                ? m.roomAnnouncementNobodyInside()
                : m.roomAnnouncementHint())}
        </p>
        <span className="shrink-0 text-muted-foreground tabular-nums">
          {text.length}/{ANNOUNCEMENT_MAX_LENGTH}
        </span>
      </div>
      <Button
        className="w-full"
        disabled={empty || here === 0 || isSending || limitReached}
        onClick={handleSend}
      >
        <Megaphone className="mr-2 h-4 w-4" />
        {m.roomAnnouncementSend({ count: here })}
      </Button>
      {announcements && announcements.length > 0 && (
        <SentAnnouncements announcements={announcements} />
      )}
    </div>
  )
}

function SentAnnouncements({ announcements }: { announcements: LiveRoomAnnouncement[] }) {
  const locale = getLocale()
  return (
    <section className="mt-2 flex flex-col gap-2">
      <h4 className="font-medium text-muted-foreground text-xs">{m.roomAnnouncementsTonight()}</h4>
      <ul className="flex flex-col gap-2">
        {announcements.map((announcement) => (
          <li key={announcement.id} className="rounded-md bg-muted px-3 py-2 text-sm">
            <p className="break-words">{announcement.text}</p>
            <time
              dateTime={announcement.createdAt}
              className="text-muted-foreground text-xs tabular-nums"
            >
              {new Date(announcement.createdAt).toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </time>
          </li>
        ))}
      </ul>
    </section>
  )
}
